import React, { useEffect, useRef, useState } from 'react';
import { Col, Form, Input, Row, Tooltip, UploadFile } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined, FieldNumberOutlined } from '@ant-design/icons';
import { clsx } from 'clsx';

import { showAxiosError } from '@utils';
import { dealerHttp, s3http } from '@network';
import { Dealer, Doctype, Document, DocumentStatus } from '@types';
import { Badge } from '@components';
import { DocumentFiles } from '@pages/credit/DocumentFiles';
import { DealerDocumentHistoryWidget } from './DealerDocumentHistoryWidget';
import { DealerDocumentStatuses } from './DealerDocumentStatuses';
import { DealerDocumentCommentForm } from './DealerDocumentCommentForm';
import cls from './DealerDoctypeDocument.module.css';

interface Props {
  doctype: Doctype;
  dealer: Dealer;
  documents: Document[];
  setDealer: () => Promise<void>;
  editable: boolean;
  downloadable?: boolean;
  uploadable?: boolean;
}

export const DealerDoctypeDocument = ({
  doctype,
  dealer,
  documents,
  setDealer,
  editable,
  downloadable,
  uploadable,
}: Props) => {
  const [form] = Form.useForm<{number: string}>();
  const [document, setDocument] = useState<Document | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const usedFiles = useRef<Record<string, boolean>>({});

  const showOptionalButton = true;
  const isNumberDoctype = doctype.isRfc || doctype.isCurp;

  let disabledOptionalButton = document ? !document?.isOptionalCheckedAvailable : false;
  if (!editable) disabledOptionalButton = true;

  const setDocumentAndNumber = () => {
    const foundDocument = documents.find(doc => doc.doctypeId === doctype.id);
    setDocument(foundDocument);

    if (isNumberDoctype) {
      const number = foundDocument?.number || '';
      form.setFieldsValue({ number });
    }
  };

  useEffect(() => void setDocumentAndNumber(), []);
  useEffect(() => void setDocumentAndNumber(), [documents]);

  const setOptionalDocument = async () => {
    setLoading(true);
    try {
      const updatedDocument = await dealerHttp.setOptionalDocument({
        dealerId: dealer.id,
        doctypeId: doctype.id,
        isOptionalChecked: document?.isOptionalChecked ? false : true,
      });

      setDocument(updatedDocument);
      await setDealer();
    } catch (err: any) {
      console.error(err);
      showAxiosError(err);
    } finally {
      setLoading(false);
    }
  };

  const onUpload = async (uploadFiles: UploadFile[]) => {
    const files: UploadFile[] = [];
    uploadFiles.forEach(file => {
      const uid = file.uid;
      if (!usedFiles.current[uid]) {
        usedFiles.current[uid] = true;
        files.push(file);
      }
    });
    if (files.length === 0) {
      return;
    }
    setLoading(true);
    try {
      const s3files = await Promise.all(
        files.map(file => s3http.uploadFile(file.originFileObj as File)),
      );
      const newIds = s3files.map(s3file => s3file.id);
      const s3Ids = (document?.files || []).map(file => file.fileId);
      const updatedDocument = await dealerHttp.saveDocuments({
        dealerId: dealer.id,
        doctypeId: doctype.id,
        fileIds: [...s3Ids, ...newIds],
        status: DocumentStatus.DOCS_SENT,
      });
      setDocument(updatedDocument);
      await setDealer();
    } catch (err: any) {
      console.error(err);
      showAxiosError(err);
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (s3FileId: number) => {
    const s3Ids = (document?.files || []).map(file => file.fileId);
    try {
      const updatedDocument = await dealerHttp.saveDocuments({
        dealerId: dealer.id,
        doctypeId: doctype.id,
        fileIds: s3Ids.filter(id => id !== s3FileId),
      });
      setDocument(updatedDocument);
      await setDealer();
    } catch (err: any) {
      showAxiosError(err);
    }
  };

  async function onNumberUpdate() {
    let values: any;

    try {
      values = await form.validateFields();
    } catch (e) {
      return;
    }

    if (!values || !values.number) {
      return;
    }

    try {
      const updatedDocument = await dealerHttp.saveDocuments({
        dealerId: dealer.id,
        doctypeId: doctype.id,
        fileIds: [],
        number: values.number,
      });
      setDocument(updatedDocument);
      await setDealer();
    } catch (err: any) {
      showAxiosError(err);
    }
  }

  const historyStatuses = (document?.history || []).filter(h => h.statusAfter !== h.statusBefore);
  const historyComments = (document?.history || []).filter(h => !!h.comment);

  const filesCount = (document?.files || []).length;
  const showFilesInput = filesCount > 0 || (!doctype.isRfc && !doctype.isCurp);
  const sendToAnalysisText = 'Required for Analysis';
  const visibilityText = `This document is ${doctype.isVisible ? '' : 'not '}visible for farmer`;

  return (
    <div className={cls.doctype}>
      <div className={cls.row}>
        <div className={cls.doctypeLeft}>
          <div className={cls.doctypeTitle}>
            {doctype.nameMx}
          </div>
        </div>
        <div className={cls.doctypeRight}>
          {doctype.isSendToCredit && (
            <Tooltip
              title={sendToAnalysisText}
              className={cls.docSendToCredit}
              placement="top"
              overlayClassName={cls.overlay}
            >
              <svg xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none">
                <path d="M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z"
                  fill="#042E6B" />
                <path fillRule="evenodd"
                  clipRule="evenodd"
                  d="M19.2917 10.625C14.5052 10.625 10.625 14.5052 10.625 19.2917C10.625 24.0781 14.5052 27.9583 19.2917 27.9583C24.0781 27.9583 27.9583 24.0781 27.9583 19.2917C27.9583 14.5052 24.0781 10.625 19.2917 10.625ZM8.625 19.2917C8.625 13.4006 13.4006 8.625 19.2917 8.625C25.1827 8.625 29.9583 13.4006 29.9583 19.2917C29.9583 25.1827 25.1827 29.9583 19.2917 29.9583C13.4006 29.9583 8.625 25.1827 8.625 19.2917Z"
                  fill="white" />
                <path fillRule="evenodd"
                  clipRule="evenodd"
                  d="M26.7816 26.8863C27.1722 26.4958 27.5082 26.094 27.5082 26.094L32.0821 30.6679C32.4726 31.0584 32.4726 31.6916 32.0821 32.0821C31.6916 32.4726 31.0584 32.4726 30.6679 32.0821L26 27.5853C26 27.5853 26.3911 27.2768 26.7816 26.8863Z"
                  fill="white" />
              </svg>
            </Tooltip>
          )}
          <Tooltip
            title={visibilityText}
            className={cls.docVisibility}
            placement="top"
            overlayClassName={cls.overlay}
          >
            {doctype.isVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          </Tooltip>
          {!document && <Badge className={cls.statusBadge} status="default" label=" " />}
          {document?.inRevision &&
            <Badge className={cls.statusBadge} status="info" label={document.statusEn} />}
          {document?.isModerated &&
            <Badge className={cls.statusBadge} status="ok" label={document.statusEn} />}
          {document?.isIncomplete &&
            <Badge className={cls.statusBadge} status="warn" label={document.statusEn} />}
          {document?.isDraft &&
            <Badge className={cls.statusBadge} status="draft" label={document.statusEn} />}
          {document?.isSigned &&
            <Badge className={cls.statusBadge} status="ok" label={document.statusEn} />}
        </div>
      </div>

      {showOptionalButton ? (
        <button
          type="button"
          className={clsx(cls.controlsOptional, document?.isOptionalChecked && cls.checked)}
          onClick={setOptionalDocument}
          disabled={loading || disabledOptionalButton}
        >Does not apply</button>
      ) : null}

      {showFilesInput ? (
        <DocumentFiles
          credit={{ id: dealer.id, user: { fullName: dealer.name || 'Dealer' } } as any}
          doctype={doctype}
          document={document}
          setDocument={setDocument}
          onUpload={onUpload}
          onDelete={onDelete}
          loading={loading}
          editable={editable}
          downloadable={downloadable}
          uploadable={uploadable}
        />
      ) : null}

      <Row gutter={[20, 20]} className={cls.info}>
        <Col xs={24} sm={12}>
          <div className={cls.infoBlock}>
            <DealerDocumentStatuses
              dealerId={dealer.id}
              document={document}
              setDocument={setDocument}
              editable={editable}
            />
          </div>
          <div className={cls.infoBlock}>
            <DealerDocumentHistoryWidget
              dealerId={dealer.id}
              history={historyStatuses}
              title="Status changed"
              setDocument={setDocument}
              isSuperadmin={true}
            />
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <div className={cls.infoBlock}>
            <DealerDocumentCommentForm
              dealerId={dealer.id}
              document={document}
              setDocument={setDocument}
              editable={editable}
            />
          </div>
          <div className={cls.infoBlock}>
            <DealerDocumentHistoryWidget
              dealerId={dealer.id}
              history={historyComments}
              title="Comments"
              setDocument={setDocument}
              isSuperadmin={true}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

const transformNumberInput = (e: any) => {
  if (e.target && e.target.value) {
    e.target.value = e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '');
  }
};

